import React, { useEffect, useState } from "react";
import { useAuthToken } from "./AuthProvider";
import { AppState } from "./store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import logo from "./img/enjuggernaut_logo.png";
import useWindowDimensions from "./Account/WindowDimensions";
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import arrow_back from "./img/arrow_back_gray.svg";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import pair_programming from "./img/pair_programming.svg";
import visibility from "./img/visibility.svg";
import visibility_off from "./img/visibility_off.svg";

export function Signup() {
  const navigate = useNavigate();
  const { updateToken, isAuthenticated, token, isLoading } = useAuthToken();
  const redirect_uri = useSelector((state: AppState) => state.redirect_uri);
  const [passwordVis, setPasswordVis] = useState<boolean>(false);

  async function googleLogin(cred: string) {
    const apiEndpoint =  "/api/google_login";

    const formData = new FormData();

    formData.append("google_token", cred);

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        body: formData,
      });

      const responseData = await response.json();

      if (response.ok) {
        updateToken(responseData.token);

        navigate(redirect_uri);
      } else {
        toast.error(responseData.error);
      }
    } catch (error) {
      toast.error("Error logging in!");
    }
  }

  const signupSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/\d/, "Password must contain at least one number")
      .matches(
        /[@$!%*?&#]/,
        "Password must contain at least one special character"
      )
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Please confirm your password"),
  });

  interface SignupFormData {
    email: string;
    password: string;
    confirmPassword: string;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: yupResolver(signupSchema),
  });

  async function submitSignup(data: SignupFormData) {
    const apiEndpoint =  "/api/signup";

    const formData = new FormData();

    formData.append("email", data.email);
    formData.append("password", data.password);

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        body: formData,
      });

      const responseData = await response.json();

      if (response.ok) {
        toast.success("Account created successfully!");
        updateToken(responseData.token);
      } else {
        toast.error(responseData.error);
      }
    } catch (error) {
      toast.error("Error creating account!");
    }
  }

  useEffect(() => {
    if (!isLoading && token != "" && token != null) {
      if (redirect_uri.length === 0 || redirect_uri === "/") {
        navigate("/resumes");
      } else {
        navigate(redirect_uri);
      }
    }
  }, [redirect_uri, token, isLoading]);

  const responseMessage = (response: CredentialResponse) => {
    googleLogin(response.credential === undefined ? "" : response.credential);
  };

  return (
    <div className="light-panel fixed min-h-screen w-full">
      <button
        className="fixed ml-4 mt-4"
        onClick={() => {
          navigate("/");
        }}
      >
        <img src={arrow_back} />
      </button>

      <div className="mx-auto mt-8 flex w-4/5 flex-row rounded-md bg-[#252526] shadow-[0_10px_100px_-2px_rgba(0,0,0,0.3)] md:w-2/3">
        <div className="w-full md:w-1/2">
          <div className="flex w-full flex-col rounded-md p-4 lg:p-6 xl:p-8 2xl:p-10">
            <img className="mx-auto w-4/5" alt="enjuggernaut logo" src={logo} />

            <form onSubmit={handleSubmit(submitSignup)} className="w-full">
              <h1 className="pt-2 font-code text-lg font-bold text-[#CCCCC1] lg:text-xl xl:pt-4 xl:text-2xl 2xl:pt-8">
                Email
              </h1>
              <input
                {...register("email")}
                className="mt-2 w-full p-2 font-code text-base lg:text-lg xl:text-xl 2xl:p-4"
              />
              {errors.email && (
                <p className="mt-2 font-code text-lg font-bold text-red-500">
                  {errors.email.message}
                </p>
              )}

              <h1 className="pt-2 font-code text-lg font-bold text-[#CCCCC1] lg:text-xl xl:pt-4 xl:text-2xl">
                Password
              </h1>
              <div className="relative flex flex-row">
                <input
                  {...register("password")}
                  type={`${passwordVis ? "text" : "password"}`}
                  className="mt-2 w-full p-2 font-code text-base lg:text-lg xl:text-xl 2xl:p-4"
                />
                <button
                  className="absolute right-0 top-4 pr-2 lg:top-5 lg:pr-4 xl:scale-150 2xl:top-7"
                  onClick={() => setPasswordVis(!passwordVis)}
                >
                  {!passwordVis && <img src={visibility}></img>}

                  {passwordVis && <img src={visibility_off}></img>}
                </button>
              </div>
              {errors.password && (
                <p className="mt-2 font-code text-lg font-bold text-red-500">
                  {errors.password.message}
                </p>
              )}

              <h1 className="pt-2 font-code text-lg font-bold text-[#CCCCC1] lg:text-xl xl:pt-4 xl:text-2xl">
                Retype Password
              </h1>
              <div className="relative flex flex-row">
                <input
                  {...register("confirmPassword")}
                  type={`${passwordVis ? "text" : "password"}`}
                  className="mt-2 w-full p-2 font-code text-base lg:text-lg xl:text-xl 2xl:p-4"
                />
                <button
                  className="absolute right-0 top-4 pr-2 lg:top-5 lg:pr-4 xl:scale-150 2xl:top-7"
                  onClick={() => setPasswordVis(!passwordVis)}
                >
                  {!passwordVis && <img src={visibility}></img>}

                  {passwordVis && <img src={visibility_off}></img>}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-2 font-code text-lg font-bold text-red-500">
                  {errors.confirmPassword.message}
                </p>
              )}

              <button
                className="start-button mx-auto mt-2 block w-1/2 p-3 font-code text-lg font-bold md:text-xl lg:text-2xl xl:mt-4 xl:text-3xl 2xl:mt-6 2xl:text-4xl"
                type="submit"
              >
                Signup
              </button>
            </form>

            <hr className="mx-auto my-4 h-1 w-48 rounded border-0 bg-gray-400 md:my-4 2xl:my-8" />

            <div className="mx-auto">
              <GoogleLogin
                size="large"
                shape="rectangular"
                onSuccess={responseMessage}
              />
            </div>

            <button
              className="pt-4 text-left font-code italic text-[#CCCCC1] 2xl:pt-8"
              onClick={() => navigate("/login")}
            >
              Have an account? Click to{" "}
              <span className="text-blue-400">log in</span>!
            </button>
          </div>
        </div>
        <div className="hidden w-1/2 flex-col p-4 md:flex lg:p-6 xl:p-8 2xl:p-10">
          <h1 className="mt-12 font-code text-lg text-[#CCCCC1] lg:text-xl xl:pt-4 xl:text-2xl">
            - <span className="text-orange">Store</span> resumes and scores
          </h1>
          <h1 className="mt-4 font-code text-lg text-[#CCCCC1] lg:text-xl xl:pt-4 xl:text-2xl">
            - <span className="text-blue">Answer questions</span> to improve and
            rewrite resumes
          </h1>
          <h1 className="mt-4 font-code text-lg text-[#CCCCC1] lg:text-xl xl:pt-4 xl:text-2xl">
            - <span className="text-orange">Match and tailor</span> resumes to
            job descriptions
          </h1>
          <img
            src={pair_programming}
            alt="pair programming"
            className="mt-auto"
          />
        </div>
      </div>
    </div>
  );
}
